<template>
  <div class="background-modal">
    <div class="wrapper-modal">
      <div class="wrapper-title">
        <p class="title">Crear imagen para inventario</p>
        <CloseButton @click="closeModal" />
      </div>

      <div class="wrapper-create-image">
        <div class="wrapper-information">
          <div class="info">
            <p>Imagen slider</p>
            <SecondaryButton
              :buttonLabel="'Subir imagen'"
              @click="$refs.fileImage1.click()"
            />
            <input
              type="file"
              ref="fileImage1"
              hidden
              @change="uploadSlider"
              @click="$event.target.value = ''"
            />
          </div>
          <p class="text">La imagen debe tener una relación 1200/430</p>

          <div class="background-image-slider">
            <figure class="grid-image">
              <img
                v-if="showSliderPreview"
                :src="previewSlider"
                class="image-slider"
              />

              <img
                v-else-if="additional?.slider"
                :src="additional.slider"
                class="image-slider"
              />

              <p v-else class="no-image">Sin imagen</p>
            </figure>

            <DeleteButton
              v-if="showSliderPreview"
              class="delete"
              @click="removeUploadSlider"
            />
          </div>
        </div>

        <div class="wrapper-information">
          <div class="info">
            <p>Imagen completa</p>
            <SecondaryButton
              :buttonLabel="'Subir imagen'"
              @click="$refs.fileImage2.click()"
            />
            <input
              type="file"
              ref="fileImage2"
              hidden
              @change="uploadBigImage"
              @click="$event.target.value = ''"
            />
          </div>

          <p class="text">La imagen debe tener una relación 1200/1440</p>

          <div class="background-image-full">
            <figure class="grid-image">
              <img
                v-if="showBigImagePreview"
                :src="previewBigImage"
                class="image-full"
              />

              <img
                v-else-if="additional?.full"
                :src="additional.full"
                class="image-full"
              />

              <p v-else class="no-image">Sin imagen</p>
            </figure>

            <DeleteButton
              v-if="showBigImagePreview"
              class="delete"
              @click="removeUploadBigImage"
            />
          </div>
        </div>
      </div>
      <StandardButton
        :buttonLabel="'Guardar'"
        class="margin-button"
        :is-disabled="disabledForm"
        @click="createEditInventoryImage"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, Ref, computed, watch } from "vue";
import StandardButton from "@/components/commons/controls/StandardButton.vue";
import SecondaryButton from "@/components/commons/controls/SecondaryButton.vue";
import CloseButton from "@/components/commons/controls/CloseButton.vue";
import { AdditionalImage } from "@/business/inventory/domain/inventory";
import { convertToBase64 } from "@/business/shared/commons/multimedia/convertToBase64";
import DeleteButton from "@/components/commons/controls/DeleteButton.vue";

export default defineComponent({
  name: "ModalCreateEditInventoryImage",
  components: {
    StandardButton,
    SecondaryButton,
    CloseButton,
    DeleteButton,
  },
  props: {
    additional: {
      type: Object as () => AdditionalImage | null,
      required: true,
    },
  },

  setup(props, context) {
    const slider: Ref<File | null> = ref(null);
    const showSliderPreview: Ref<boolean> = ref(false);
    const previewSlider: Ref<string> = ref("");

    const bigImage: Ref<File | null> = ref(null);
    const showBigImagePreview: Ref<boolean> = ref(false);
    const previewBigImage: Ref<string> = ref("");

    const isEdit: Ref<boolean> = ref(false);

    watch(
      () => props.additional,
      () => {
        removeFiles();

        if (props.additional != null) {
          isEdit.value = true;
        } else {
          isEdit.value = false;
        }
      },
      { immediate: true }
    );

    function closeModal() {
      removeFiles();
      context.emit("closeImageInventory");
    }

    async function uploadSlider(event: any) {
      slider.value = event.target.files[0];

      if (slider.value !== null) {
        previewSlider.value = await convertToBase64.execute(slider.value);
        showSliderPreview.value = true;
      }
    }

    function removeUploadSlider(): void {
      previewSlider.value = "";
      slider.value = null;
      showSliderPreview.value = false;
    }

    async function uploadBigImage(event: any) {
      bigImage.value = event.target.files[0];

      if (bigImage.value !== null) {
        previewBigImage.value = await convertToBase64.execute(bigImage.value);

        showBigImagePreview.value = true;
      }
    }

    function removeUploadBigImage(): void {
      previewBigImage.value = "";
      bigImage.value = null;
      showBigImagePreview.value = false;
    }

    function removeFiles(): void {
      removeUploadSlider();
      removeUploadBigImage();
    }

    function createEditInventoryImage(): void {
      const inventoryImageForm = {
        bigImage: bigImage.value,
        slider: slider.value,
        isEdit: isEdit.value,
      };

      context.emit("secondary", inventoryImageForm);

      removeFiles();
    }

    const disabledForm = computed(() => {
      return isEdit.value
        ? slider.value == null && bigImage.value == null
        : slider.value == null || bigImage.value == null;
    });

    return {
      closeModal,

      slider,
      bigImage,

      uploadSlider,
      removeUploadSlider,
      uploadBigImage,
      removeUploadBigImage,

      showSliderPreview,
      previewSlider,

      showBigImagePreview,
      previewBigImage,

      disabledForm,
      createEditInventoryImage,
    };
  },
});
</script>

<style scoped lang="scss">
@import "src/ux/sampleBook/styles/modalCreateEditInventoryImageStyle";
</style>
